import queryString from 'query-string';
import isEqual from 'lodash.isequal';
import transformValueForUrl from './transform-to-url';
import { Filters, SchemaFilterType, SchemaProps } from '../types';

const formatSearch = ({
  currentSchemaValues,
  searchParams,
}: {
  currentSchemaValues?: SchemaProps;
  searchParams: URLSearchParams;
}) => {
  const currentFiltersParams: Record<string, {
      value: any;
      type: SchemaFilterType;
    }> = {
    sortDir: currentSchemaValues?.sortDir?.value?.default || '',
    sortProp: currentSchemaValues?.sortProp?.value?.default || '',
  };

  const keys: {
    key: string;
    value: string;
  }[] = [];

  if (currentSchemaValues) {
    searchParams.forEach((value, key) => {
      keys.push({
        key,
        value,
      });

      const currentSchema = currentSchemaValues[key];

      if (currentSchema) {
        if (currentSchema?.type === SchemaFilterType.DATE_RANGE) {
          let rangeSchema: {
            queryNames?: string[];
            name?: string;
          } = {};

          Object.keys(currentSchemaValues).forEach((item) => {
            if (currentSchemaValues[item].queryNames) {
              if (currentSchemaValues[item]?.queryNames?.includes(key)) {
                rangeSchema = {
                  queryNames: currentSchemaValues[item]?.queryNames,
                  name: item,
                };
              }
            }
          });

          if (rangeSchema?.queryNames && key === rangeSchema?.queryNames[0]) {
            currentFiltersParams[rangeSchema?.name as string] = {
              value: [value],
              type: SchemaFilterType.DATE_RANGE,
            };
          } else if (keys.filter((item) => item.key === key)) {
            currentFiltersParams[rangeSchema?.name as string] = {
              value: [
                ...currentFiltersParams[rangeSchema?.name as string].value,
                value,
              ],
              type: SchemaFilterType.DATE_RANGE,
            };
          }
        } else if (keys.filter((item) => item.key === key)) {
          const values = keys.filter((item) => item.key === key);
          const currentType = currentSchemaValues[key]?.type;

          currentFiltersParams[key] = {
            value: values.map((item) => item.value),
            type: currentType || SchemaFilterType.STRING,
          };
        }
      }
    });
  }

  return currentFiltersParams;
};

export default function formatSearchParams({
  currentSchemaValues,
  storageKey,
  search,
}: {
  currentSchemaValues?: SchemaProps;
  storageKey: string;
  search?: string;
}) {
  const currentFilters = localStorage.getItem(storageKey);

  let parsed: Filters = {};

  if (currentFilters) {
    parsed = JSON.parse(currentFilters);
  }

  const savedFilters = transformValueForUrl(
    parsed,
    currentSchemaValues as SchemaProps,
  );

  const parsedQuery = queryString.parse((search as string) || '');
  const parsedQueryWithoutSort: Record<string, string | (string | null)[] | null> = {};
  const savedSearchParams = new URLSearchParams();
  const nextSearchParams = new URLSearchParams();

  Object.entries(parsedQuery).forEach(([key, filterValue]) => {
    if (key !== 'sortProp' && key !== 'sortDir') {
      parsedQueryWithoutSort[key] = filterValue;
    }
  });

  if (Object.keys(parsedQueryWithoutSort).length > 0) {
    Object.entries(savedFilters).forEach(([key, filterValue]) => {
      if (filterValue && filterValue.length) {
        if (Array.isArray(filterValue) && filterValue.length > 1) {
          filterValue.forEach((item: string) =>
            savedSearchParams.append(key, item),
          );
        } else {
          savedSearchParams.append(key, filterValue);
        }
      }
    });

    Object.entries(parsedQuery).forEach(([key, filterValue]) => {
      if (filterValue) {
        if (Array.isArray(filterValue)) {
          (filterValue as string[]).forEach((item: string) =>
            nextSearchParams.append(key, item),
          );
        } else {
          nextSearchParams.append(key, filterValue);
        }
      }
    });

    const savedFiltersValues = formatSearch({
      currentSchemaValues,
      searchParams: savedSearchParams,
    });

    const currentFiltersValues = formatSearch({
      currentSchemaValues,
      searchParams: nextSearchParams,
    });

    const formattedSavedFilters: Record<string, any> = {};

    const formattedCurrentFilter: Record<string, any> = {};

    Object.entries(savedFiltersValues).forEach(([key, filterValue]) => {
      if (key !== 'sortProp' && key !== 'sortDir') {
        formattedSavedFilters[key] = filterValue;
      }
    });

    Object.entries(currentFiltersValues).forEach(([key, filterValue]) => {
      if (key !== 'sortProp' && key !== 'sortDir') {
        formattedCurrentFilter[key] = filterValue;
      }
    });

    if (
      !isEqual(formattedSavedFilters, formattedCurrentFilter) ||
      !currentFilters
    ) {
      localStorage.setItem(storageKey, JSON.stringify(currentFiltersValues));
    }
  }
}
